import React from 'react';
import { LOGO_URL, BADGE_URL } from '../../data/cdaContent';
import { useLang } from '../../i18n/cda';
import { Button } from '../ui/button';
import { Facebook, Instagram, Share2, Download } from 'lucide-react';
import { toast } from 'sonner';

export const CdaLogo = ({ size = 36, showText = true, textClass = 'text-slate-900' }) => (
  <div className="flex items-center gap-2.5">
    <img src={LOGO_URL} alt="Care Comply" style={{ width: size, height: size }} className="object-contain" />
    {showText && (
      <span className={`font-bold text-lg tracking-tight ${textClass}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
        Care&nbsp;Comply
      </span>
    )}
  </div>
);

const SHARE_CAPTION = "I'm earning my CDA credential with Care Comply! 🎓 Taking the next step in my early childhood career. #CDA #ChildDevelopment #CareComply";

export const ShareBadge = ({ compact = false }) => {
  const { t } = useLang();
  const shareUrl = typeof window !== 'undefined' ? window.location.origin + '/cda' : 'https://carecomply.app/cda';

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "I'm earning my CDA!", text: SHARE_CAPTION, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      navigator.clipboard?.writeText(`${SHARE_CAPTION} ${shareUrl}`);
      toast.success(t('Caption & link copied — paste it on your favorite app!'));
    }
  };

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(SHARE_CAPTION)}`, '_blank', 'width=600,height=500');
  };

  const shareInstagram = async () => {
    // Instagram doesn't have a direct web share API, so we download the badge and copy caption
    try {
      const res = await fetch(BADGE_URL);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'im-earning-my-cda-badge.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Copy caption to clipboard
      navigator.clipboard?.writeText(SHARE_CAPTION);
      toast.success(t('Badge downloaded & caption copied! Open Instagram and create a new post 📸'));
    } catch (e) {
      window.open(BADGE_URL, '_blank');
      navigator.clipboard?.writeText(SHARE_CAPTION);
      toast.success(t('Caption copied! Save the badge image and post it on Instagram 📸'));
    }
  };

  const downloadBadge = async () => {
    try {
      const res = await fetch(BADGE_URL);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'im-earning-my-cda-badge.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('Badge downloaded! Post it and tag your friends 💚'));
    } catch (e) {
      window.open(BADGE_URL, '_blank');
    }
  };

  return (
    <div className={`bg-white rounded-2xl border border-emerald-100 ${compact ? 'p-4' : 'p-5'} shadow-sm`} data-testid="share-badge-card">
      <div className="flex items-center gap-4">
        <img src={BADGE_URL} alt="I'm earning my CDA badge" className={`${compact ? 'w-20 h-20' : 'w-24 h-24'} rounded-xl object-cover shrink-0 shadow`} />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-slate-900">{t("Show your community you're on the way! 🌟")}</p>
          <p className="text-sm text-slate-600 mb-3">{t('Share your "I\'m Earning My CDA" badge — it\'s perfect to post alongside your GoFundMe.')}</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={shareFacebook} className="bg-[#1877F2] hover:bg-[#0f63d6]" data-testid="share-facebook-button">
              <Facebook className="w-4 h-4 mr-1.5" /> {t('Facebook')}
            </Button>
            <Button size="sm" onClick={shareInstagram} className="bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] hover:opacity-90" data-testid="share-instagram-button">
              <Instagram className="w-4 h-4 mr-1.5" /> {t('Instagram')}
            </Button>
            <Button size="sm" variant="outline" onClick={nativeShare} data-testid="share-native-button">
              <Share2 className="w-4 h-4 mr-1.5" /> {t('Share')}
            </Button>
            <Button size="sm" variant="outline" onClick={downloadBadge} data-testid="share-download-button">
              <Download className="w-4 h-4 mr-1.5" /> {t('Save badge')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CdaLogo;
